import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import CommentsAPI from "../../api/CommentsAPI";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { CommentsQuery } from "./useFetchComments";
import useUser from "../auth/useUser";
import { isEmpty } from "underscore";

interface Props {
  commentID: string;
  context: string;
  contextID: string;
  reaction: string;
}

// For adding AND removing reactions
// Uses optimistic updates, no error handling implemented yet
export default function useCommentReaction() {
  const client = useQueryClient();
  const { user } = useUser();

  const reactToComment = useMutation({
    mutationFn: ({ commentID, reaction }: Props) =>
      CommentsAPI.reactToComment(commentID, reaction),
    onMutate: ({ reaction, context, contextID, commentID }) => {
      client.setQueryData<InfiniteData<CommentsQuery>>(
        ["comments", context, contextID],
        (prev) => {
          if (!prev) return prev;

          for (const page of prev.pages) {
            const foundComment = page.comments.find((c) => c._id === commentID);

            if (!foundComment) continue;

            if (!foundComment?.Reactions) {
              foundComment.Reactions = {};
            }

            if (foundComment?.Reactions?.[reaction]?.includes(user.id)) {
              foundComment.Reactions[reaction] = foundComment.Reactions[
                reaction
              ].filter((id) => id !== user.id);
            } else if (Array.isArray(foundComment?.Reactions?.[reaction])) {
              foundComment.Reactions[reaction] = [
                ...foundComment.Reactions[reaction],
                user.id,
              ];
            } else if (
              isEmpty(foundComment?.Reactions) ||
              !foundComment?.Reactions?.[reaction]
            ) {
              foundComment.Reactions[reaction] = [user.id];
            }
          }

          return prev;
        }
      );
    },
    onError: (err) => {
      console.log(getErrorMessage(err));
    },
  });

  return {
    reactToComment: reactToComment.mutate,
  };
}
